import { useMemo } from "react"
import { useAdForgeGetDefaultsQuery } from "~/ui-rtk/api/adForgeApi"
import { useGetTagCategoriesQuery } from "~/ui-rtk/api/tagCategoriesApi"
import { extractSlugs } from "./adNameTemplate"

/**
 * The company's required-tags rule. Tagging is MANDATORY: AdForge blocks "Save
 * as draft" (`validate`) and AdBlaster blocks the blast (`missingFor`).
 *
 * Two tiers:
 *   1. Every tag category the company's ad-name template references. The
 *      template IS the naming convention, so a `{gender}` placeholder means
 *      every ad name claims a gender — launching without that tag gives you a
 *      name that lies and an ad that can't be sliced by that dimension.
 *   2. With no template configured, no category is named, but an ad still has to
 *      carry at least one tag.
 *
 * Editing the template in AdBlaster's "AdForge defaults" modal changes what's
 * required; there's no second list to keep in sync. Two cases demand nothing: a
 * company with no tag categories, and the window before both queries settle (so
 * a slow request never blocks a save with a phantom requirement).
 *
 * The server enforces tier 1 in `prepareBlastAdCreative` (see
 * `ad-creative-tags.util.ts`) — pre-spend, so a blocked blast costs nothing.
 * Tier 2 is authoring-time only: drafts saved before tags existed have none,
 * and stranding them in AdBlaster would help no one.
 */
export interface RequiredAdTags {
  /** Category slugs the template references, in template order. */
  requiredSlugs: string[]
  /** Human label for a slug (falls back to the slug for stale placeholders). */
  labelForSlug: (slug: string) => string
  /** Required slugs with no value in `tags`, in template order. */
  missingFor: (tags: Record<string, string[]> | null | undefined) => string[]
  /**
   * Whether tagging is mandatory at all. False only for a company with no tag
   * categories — nothing to pick, so nothing can be demanded.
   */
  isTaggingRequired: boolean
  /**
   * Why a selection isn't good enough to save, or null when it passes.
   *
   * `missing` — specific templated categories with no value.
   * `needsAnyTag` — no template configured, so no category is named, but at
   * least one tag is still required (this is the "you must tag your ads" floor).
   */
  validate: (
    tags: Record<string, string[]> | null | undefined,
  ) => { missing: string[]; needsAnyTag: boolean } | null
  /** True once both the template and categories have loaded. */
  isReady: boolean
}

export function useRequiredAdTags(options?: {
  skip?: boolean
}): RequiredAdTags {
  const skip = options?.skip ?? false
  const { data: defaults, isSuccess: defaultsLoaded } =
    useAdForgeGetDefaultsQuery(undefined, { skip })
  const { data: categories, isSuccess: categoriesLoaded } =
    useGetTagCategoriesQuery(undefined, { skip })

  return useMemo(() => {
    const labelBySlug = new Map(
      (categories ?? []).map(category => [category.slug, category.label]),
    )
    // Only categories that actually exist can be required. A stale `{slug}` in
    // the template (the defaults modal warns about these) has nothing to pick in
    // the builder, so demanding it would deadlock every save.
    const requiredSlugs = extractSlugs(
      defaults?.nameTemplate?.trim() ?? "",
    ).filter(slug => labelBySlug.has(slug))
    const isReady = defaultsLoaded && categoriesLoaded
    // A company with zero tag categories has nothing to pick, so tagging can't
    // be demanded. Until the queries settle we also treat it as not-required so
    // a slow request never blocks a save with a phantom requirement.
    const isTaggingRequired = isReady && (categories ?? []).length > 0
    const missingFor = (tags: Record<string, string[]> | null | undefined) =>
      requiredSlugs.filter(slug => !(tags?.[slug]?.length ?? 0))

    return {
      requiredSlugs,
      labelForSlug: (slug: string) => labelBySlug.get(slug) ?? slug,
      missingFor,
      isTaggingRequired,
      validate: tags => {
        if (!isTaggingRequired) return null
        const missing = missingFor(tags)
        if (missing.length > 0) return { missing, needsAnyTag: false }
        // No template → no named categories, but an ad still has to carry at
        // least one tag.
        const hasAnyTag = Object.values(tags ?? {}).some(ids => ids.length > 0)
        if (requiredSlugs.length === 0 && !hasAnyTag) {
          return { missing: [], needsAnyTag: true }
        }
        return null
      },
      isReady,
    }
  }, [defaults?.nameTemplate, categories, defaultsLoaded, categoriesLoaded])
}
